import { useAtom, useAtomValue } from 'jotai'
import { motion } from 'motion/react'
import { LiveField, StatefulImage } from '../../rapid-ui'
import { useLogoUpload } from '../hooks/useLogoUpload'
import { DefaultAttribution } from '../simulator-tools/attribution'
import { EditableComplexText } from '../simulator-tools/editable'
import {
	campaignDetailsBrandingAtom,
	feedAdCtaTextAtom,
	feedAdLocationAtom,
	feedAdMetaSubtitleTextLayerAtom,
	feedAdMetaSubtitleTextTransformAtom,
	feedAdMetaTitleTextLayerAtom,
	feedAdMetaTitleTextTransformAtom,
	feedAdSubtitleAtom,
	feedAdTitleAtom,
} from '../state'

type CommonContentProps = {
	isGeneratingText: boolean
}

export function ContentLogo() {
	const activeBranding = useAtomValue(campaignDetailsBrandingAtom)
	const { triggerUpload, isUploading, fileInput } = useLogoUpload()

	return (
		<motion.div layoutId="feed-ad-logo">
			<motion.div
				layout
				className="bg-white rounded-xl size-24 p-2 z-50"
				style={{
					backgroundColor: 'var(--logo-background-fill-color)',
				}}
			>
				<StatefulImage
					src={activeBranding?.logo?.url ?? ''}
					alt="Logo"
					className="h-full w-full cursor-pointer"
					style={{ backgroundColor: 'transparent' }}
					objectFit="contain"
					onClick={triggerUpload}
					forceIsLoading={isUploading}
				/>
			</motion.div>
			{fileInput}
		</motion.div>
	)
}

export function ContentLocation({ isGeneratingText }: CommonContentProps) {
	const [location, setLocation] = useAtom(feedAdLocationAtom)

	return (
		<motion.div layoutId="feed-ad-location">
			<motion.div layout className="relative z-50 text-sm flex items-center h-[max-content] gap-2">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					id="Maps-Pin-3--Streamline-Nova"
					height={12}
					width={12}
				>
					<path
						d="M12 0.91a9.011 9.011 0 0 0 -9 9c0 7.469 8.124 12.632 8.47 12.848l0.53 0.332 0.53 -0.332C12.876 22.542 21 17.379 21 9.91a9.011 9.011 0 0 0 -9 -9zm0 14a5 5 0 1 1 5 -5 5.006 5.006 0 0 1 -5 5z"
						fill="currentColor"
						strokeWidth={1}
					/>
				</svg>
				<LiveField
					isGenerating={isGeneratingText}
					value={location ?? ''}
					onChange={setLocation}
					style={{
						textShadow:
							'0 0 2px rgba(0, 0, 0, 0.7), 0 0 8px rgba(0, 0, 0, 0.3), 0 0 16px rgba(0, 0, 0, 0.6)',
					}}
				/>
			</motion.div>
		</motion.div>
	)
}

export function ContentTitle({ isGeneratingText }: CommonContentProps) {
	const [title, setTitle] = useAtom(feedAdTitleAtom)
	const [titleTextTransform, setMetaTitleTextTransform] = useAtom(feedAdMetaTitleTextTransformAtom)
	const [titleTextLayer, setMetaTitleTextLayer] = useAtom(feedAdMetaTitleTextLayerAtom)

	if (!titleTextTransform || !titleTextLayer) {
		return null
	}

	return (
		<EditableComplexText
			className="text-xl"
			focusTargetId={{ target: 'feed-ad(contentTitle)' }}
			transform={titleTextTransform}
			overrideValue={true}
			textLayer={titleTextLayer}
			value={title ?? ''}
			onChange={setTitle}
			isGenerating={isGeneratingText}
			onTransformChange={setMetaTitleTextTransform}
			onTextLayerChange={setMetaTitleTextLayer}
		/>
	)
}

export function ContentSubtitle({ isGeneratingText }: CommonContentProps) {
	const [subtitle, setSubtitle] = useAtom(feedAdSubtitleAtom)
	const [subtitleTextTransform, setMetaSubtitleTextTransform] = useAtom(feedAdMetaSubtitleTextTransformAtom)
	const [subtitleTextLayer, setMetaSubtitleTextLayer] = useAtom(feedAdMetaSubtitleTextLayerAtom)

	if (!subtitleTextTransform || !subtitleTextLayer) {
		return null
	}

	return (
		<EditableComplexText
			className="text-sm"
			focusTargetId={{ target: 'feed-ad(contentSubtitle)' }}
			transform={subtitleTextTransform}
			overrideValue={true}
			textLayer={subtitleTextLayer}
			value={subtitle ?? ''}
			onChange={setSubtitle}
			isGenerating={isGeneratingText}
			onTransformChange={setMetaSubtitleTextTransform}
			onTextLayerChange={setMetaSubtitleTextLayer}
		/>
	)
}

export function ContentCta({ isGeneratingText }: CommonContentProps) {
	const [ctaText, setCtaText] = useAtom(feedAdCtaTextAtom)
	const branding = useAtomValue(campaignDetailsBrandingAtom)

	return (
		<div
			className="text-grey-600 py-2 px-4 rounded w-[max-content] transition-all"
			style={{
				backgroundColor: branding?.accentBackground,
				color: branding?.accentForeground,
			}}
		>
			<LiveField
				isGenerating={isGeneratingText}
				value={ctaText ?? ''}
				onChange={setCtaText}
			/>
		</div>
	)
}

export function ContentAttribution() {
	return (
		<motion.div
			layoutId="feed-ad-attribution"
			className="relative flex justify-end z-50"
		>
			<DefaultAttribution />
		</motion.div>
	)
}
